import type { ReactNode } from "react";
import { SlideHeader } from "../../../SlideHeader";

const TILES: Array<{
  icon: string[];
  label: string;
  win: string;
  how: string;
}> = [
  {
    icon: ["m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"],
    label: "AI Discoverability",
    win: "Found where users now look",
    how: "Broad-based GEO (generative engine optimization): audited across ChatGPT, Claude, Gemini, Perplexity, Reddit, publisher websites & mentions across the web — not just the stores.",
  },
  {
    icon: [
      "M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z",
      "M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z",
    ],
    label: "Competitors",
    win: "Never blindsided",
    how: "Every rival's metadata, ranking & asset change, surfaced the day it's live.",
  },
  {
    icon: [
      "M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 0 1-.923 1.785A5.969 5.969 0 0 0 6 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337Z",
    ],
    label: "User Sentiment",
    win: "Reviews become a roadmap",
    how: "Both stores scored continuously, distilled into themes and actions.",
  },
  {
    icon: [
      "M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418",
    ],
    label: "Localization",
    win: "Native-quality in every market",
    how: "Each revision auto-translated, grounded in per-market analysis.",
  },
  {
    icon: [
      "M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75",
    ],
    label: "Store Algorithms",
    win: "In step as ranking shifts",
    how: "Listings continuously re-tuned to how the stores actually rank.",
  },
];

export default function TheCustomerWin() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-8 md:[&_header]:mb-0">
      <SlideHeader eyebrow="Customer's Win">
        <h1 className="text-balance text-3xl font-semibold leading-tight tracking-tight md:text-6xl md:leading-[1.1]">
          Activate Apptonomy.
          <br />
          Rank higher. Convert better.
        </h1>
        <p className="mt-3 text-xl font-medium tracking-tight text-slate-700 md:mt-4 md:text-3xl">
          On every app in your portfolio, every week — consistently
          outperforming the competition.
        </p>
      </SlideHeader>

      {/* Tiles fill the middle band so there's no dead space */}
      <div className="grid grid-cols-1 gap-3 py-6 md:grid-cols-5 md:gap-4 md:py-0 md:min-h-0 md:flex-1 md:content-center">
        {TILES.map((tile) => (
          <div
            key={tile.label}
            className="flex flex-col rounded-2xl bg-white p-5 text-left shadow-sm ring-1 ring-slate-200 md:p-6"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600 ring-1 ring-blue-100 md:h-12 md:w-12">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.6}
                aria-hidden="true"
                className="h-6 w-6"
              >
                {tile.icon.map((d) => (
                  <path
                    key={d}
                    d={d}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                ))}
              </svg>
            </span>
            <p className="mt-4 text-[10px] font-semibold uppercase tracking-[0.14em] text-blue-600 md:text-xs">
              {tile.label}
            </p>
            <p className="mt-1.5 text-base font-bold leading-tight tracking-tight text-slate-900 md:text-lg">
              {tile.win}
            </p>
            <p className="mt-2 text-xs leading-snug text-slate-600 md:text-sm">
              {tile.how}
            </p>
          </div>
        ))}
      </div>

      {/* Grounded closing band */}
      <div className="mt-4 rounded-2xl bg-slate-50 px-6 py-5 text-center ring-1 ring-slate-200 md:mt-5 md:px-10 md:py-6">
        <p className="text-balance text-2xl font-bold tracking-tight text-slate-900 md:text-4xl">
          …across every app in your portfolio.
        </p>
        <p className="mt-4 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400 md:mt-5 md:text-sm">
          And it doesn't stop at advice
        </p>
        <div className="mt-2.5 flex flex-wrap items-center justify-center gap-2.5 md:mt-3 md:gap-3">
          <Chip>One-click publishing to both stores</Chip>
          <Chip>Impact proven on your real store data</Chip>
        </div>
      </div>
    </div>
  );
}

function Chip({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-blue-700 shadow-sm ring-1 ring-blue-200 md:px-5 md:py-2.5 md:text-base">
      <CheckIcon />
      {children}
    </span>
  );
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
      className="h-4 w-4 shrink-0 text-blue-600 md:h-5 md:w-5"
    >
      <path
        fillRule="evenodd"
        d="M16.707 5.293a1 1 0 0 1 0 1.414l-8 8a1 1 0 0 1-1.414 0l-4-4a1 1 0 1 1 1.414-1.414L8 12.586l7.293-7.293a1 1 0 0 1 1.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}
